import formatSizes from "~/constants/format-sizes"
import { getLayflatSpineWidthMm, getSpineWidthMm } from "~/config/printingConfig"
import { resolveSceneFrame } from "~/utils/sceneFrame"

type GuideFrame = {
  width: number
  height: number
}

type EditorGuideScene = {
  id?: string
  frame?: GuideFrame
  layers?: any[]
}

type BleedGuideConfig = {
  type: "inside" | "cover"
  bleedPx: number
  frameWidth: number
  frameHeight: number
  spinePx?: number
  creasePx?: number
  showCenterLine?: boolean
  showSpineGuides?: boolean
  showSafeZone?: boolean
}

type SafeZoneConfig = {
  enabled: boolean
  left: number
  top: number
  right: number
  bottom: number
  type: "inside" | "cover" | "none"
  regions?: Array<{
    id: string
    left: number
    top: number
    right: number
    bottom: number
  }>
}

type GuideConfigResult = {
  bleedGuideConfig: BleedGuideConfig | null
  safeZoneConfig: SafeZoneConfig
}

const EMPTY_SAFE_ZONE_CONFIG: SafeZoneConfig = {
  enabled: false,
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  type: "none",
}

const mmToPx = (mm: number) => (mm * 300) / 25.4

const getFormatEntry = (sku: string) =>
  formatSizes.find((format: any) => format.sku.some((candidate: string) => sku === candidate)) ||
  formatSizes.find((format: any) => format.sku.some((candidate: string) => sku.startsWith(candidate)))

const hasPersonalizedCover = (sku: string, coverFinish: string) => {
  const normalizedFinish = coverFinish.toLowerCase()
  return sku.includes("PH") || normalizedFinish.includes("personalized") || normalizedFinish.includes("personalised")
}

export const getEditorGuideConfigs = ({
  sku,
  coverFinish,
  scenes,
  scene,
  frame,
}: {
  sku: string
  coverFinish: string
  scenes: EditorGuideScene[]
  scene?: EditorGuideScene | null
  frame?: Partial<GuideFrame>
}): GuideConfigResult => {
  if (!scene) {
    return {
      bleedGuideConfig: null,
      safeZoneConfig: EMPTY_SAFE_ZONE_CONFIG,
    }
  }

  const normalizedSku = sku.toUpperCase()
  const formatEntry = getFormatEntry(normalizedSku)
  const resolvedFrame = {
    ...resolveSceneFrame(scene, frame),
    ...frame,
  }

  const frameWidth = Number(resolvedFrame.width || 0)
  const frameHeight = Number(resolvedFrame.height || 0)

  if (!frameWidth || !frameHeight) {
    return {
      bleedGuideConfig: null,
      safeZoneConfig: EMPTY_SAFE_ZONE_CONFIG,
    }
  }

  const sceneIndex = scenes.findIndex((entry) => entry.id === scene.id)
  const isCoverScene = hasPersonalizedCover(normalizedSku, coverFinish) && sceneIndex === 0

  if (isCoverScene) {
    const coverSize = formatEntry?.coverSize
    const bleedMm = typeof coverSize?.bleed === "number" ? coverSize.bleed : 10
    const creaseMm = 10
    const spreadCount = Math.max(0, scenes.length - 1)
    const spineMm =
      spreadCount > 0
        ? normalizedSku.includes("LAY")
          ? getLayflatSpineWidthMm(spreadCount)
          : getSpineWidthMm(spreadCount)
        : 8
    const bleedPx = mmToPx(bleedMm)
    const spinePx = mmToPx(spineMm)
    const creasePx = mmToPx(creaseMm)
    const coverHalfW = Math.round((frameWidth - spinePx) / 2)
    const spineLeft = coverHalfW
    const frontLeft = coverHalfW + spinePx

    return {
      bleedGuideConfig: {
        type: "cover",
        bleedPx,
        frameWidth,
        frameHeight,
        spinePx,
        creasePx,
        showSafeZone: false,
      },
      safeZoneConfig: {
        enabled: true,
        left: 0,
        top: 0,
        right: frameWidth,
        bottom: frameHeight,
        type: "cover",
        regions: [
          {
            id: "back-panel",
            left: 0,
            top: 0,
            right: coverHalfW,
            bottom: frameHeight,
          },
          {
            id: "front-panel",
            left: frontLeft,
            top: 0,
            right: frameWidth,
            bottom: frameHeight,
          },
          {
            id: "back-safe",
            left: bleedPx,
            top: bleedPx,
            right: coverHalfW - creasePx,
            bottom: frameHeight - bleedPx,
          },
          {
            id: "front-safe",
            left: frontLeft + creasePx,
            top: bleedPx,
            right: frameWidth - bleedPx,
            bottom: frameHeight - bleedPx,
          },
          {
            id: "spine",
            left: spineLeft,
            top: bleedPx,
            right: spineLeft + spinePx,
            bottom: frameHeight - bleedPx,
          },
        ],
      },
    }
  }

  const pdfPage = formatEntry?.printSpecs?.pdfPage
  const jpegSpread = formatEntry?.printSpecs?.jpegSpread
  const specWidth = Number(pdfPage?.width || jpegSpread?.width || 0)
  const bleedMm = Number(pdfPage?.bleed || jpegSpread?.bleed || 3)

  if (!specWidth) {
    return {
      bleedGuideConfig: null,
      safeZoneConfig: EMPTY_SAFE_ZONE_CONFIG,
    }
  }

  const pxPerMm = frameWidth / specWidth
  const bleedPx = bleedMm * pxPerMm

  return {
    bleedGuideConfig: {
      type: "inside",
      bleedPx,
      frameWidth,
      frameHeight,
      showCenterLine: normalizedSku.includes("LAY"),
      showSafeZone: true,
    },
    safeZoneConfig: {
      enabled: true,
      left: bleedPx,
      top: bleedPx,
      right: frameWidth - bleedPx,
      bottom: frameHeight - bleedPx,
      type: "inside",
    },
  }
}
